import { PropsWithChildren, useRef, useState } from "react";
import { API_AccountSecurityGenerateCode, API_AccountSecurityRemoveMfaType, API_AccountSecurityVerifyMfaType, MfaStateModel, SetupTypeResponseModel } from "../../../apis/ApisAccountSecurity";
import { ErrorsDisplay } from "../Shared/Error";
import toast from "react-hot-toast";

export type CardProps = {
    baseState: MfaStateModel,
    reload: () => void,
}

export function SetupAppMFAForm(props: PropsWithChildren<CardProps>) {
    const [qrurl, setQrurlg] = useState<string>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [enabled] = useState<boolean>(props.baseState.Enabled);
    const [canBeRemoved] = useState<boolean>(props.baseState.CanBeRemoved);
    const [userMFAVerified, setUserMFAVerified] = useState<boolean>(props.baseState.UserMFAVerified);
    const [setupTypeResponseModel, setSetupTypeResponseModel] = useState<SetupTypeResponseModel>(null);
    const inputMFARef = useRef<HTMLInputElement>(null);
    const inputMFARemoveRef = useRef<HTMLInputElement>(null);
    const [inputMFA, setInputMFA] = useState<string>('');
    const [errors, setErrors] = useState<string[]>([]);

    var inSetup = setupTypeResponseModel == null ? false : true;
    const QRCode = require('qrcode');

    if (loading) {
        return (<div className="d-flex justify-content-center"><i className="fa-solid fa-spinner fa-spin fa-lg"></i></div>);
    }

    const beginSetup = async () => {
        try {
            setLoading(true);
            var res = await API_AccountSecurityGenerateCode({ Type: props.baseState.Type.Type, Code: "" });
            setSetupTypeResponseModel(res);
            QRCode.toDataURL(res.ImageUrl, {
                errorCorrectionLevel: 'H',
                type: 'image/jpeg',
                quality: 0.3,
                margin: 1,
                width: 200,
                height: 200,
                color: {
                    dark: "#000",
                    light: "#fff"
                }
            }, function (err: any, url: any) {
                if (err) throw err
                setQrurlg(url)
            })
        }
        catch {
            toast.error("Error");
            setErrors(["Error generating code"]);
            setSetupTypeResponseModel({ ImageUrl: "", Message: "", Secret: "", Completed: false });
        }
        finally {
            setLoading(false);
        }
    }

    const submitCode = async () => {
        if (inputMFA != '' && inputMFA.length > 0) {
            try {
                setLoading(true);
                var res = await API_AccountSecurityVerifyMfaType({ Type: props.baseState.Type.Type, Code: inputMFA });
                if (res.Completed) {
                    inputMFARef?.current?.classList.remove("is-invalid");
                    inputMFARef?.current?.classList.remove("is-valid");
                    setUserMFAVerified(true);
                    toast.success("Setup");
                    setErrors([]);

                    await props.reload();
                }
                else {
                    inputMFARef?.current?.classList.add("is-invalid");
                    inputMFARef?.current?.classList.remove("is-valid");
                    setErrors(["Code is invalid"]);
                }
            }
            catch {
                setErrors(["Error submitting code"]);
                toast.error("Error");
            }
            finally {
                setInputMFA('');
                setLoading(false);
            }
        }
        else {
            inputMFARef?.current?.classList.add("is-invalid");
            inputMFARef?.current?.classList.remove("is-valid");
            setErrors(["Please provide a code"]);
            setInputMFA('');
        }
    }

    if (!userMFAVerified && enabled && !inSetup) {
        return (
            <>
                <h5 className="text-alt-or-contrast">QR code generation needed</h5>
                <p>To link an authentication app you need to generate a QR code.</p>
                <button onClick={beginSetup} className="btn btn-secondary">Generate</button>
            </>
        );
    }

    if (!userMFAVerified && enabled && inSetup) {
        return (
            <form>
                <div className="row">
                    <div className="col-lg-3 col-md-4 text-center">
                        <img id="qrcode" src={qrurl}></img>
                    </div>
                    <div className="col-lg-9 col-md-8 d-flex flex-column  justify-content-between">

                        <h5 className="text-alt-or-contrast">Setting up authentication app</h5>
                        <p>{setupTypeResponseModel.Message} <br />
                            <strong className="text-alt-or-contrast">Secret Key:</strong> {setupTypeResponseModel.Secret}<br />
                            Please enter the code generated by your authenticator app below to setup this type of authentication for your account.
                        </p>

                        <ErrorsDisplay errors={errors} />

                        <label htmlFor="CodeInput" className="form-label">Code</label>
                        <div className="input-group">
                            <input type="text" ref={inputMFARef} className="form-control" placeholder="Code" id="CodeInput" onChange={t => {
                                setInputMFA(t.currentTarget.value);
                                inputMFARef?.current?.classList.remove("is-valid");
                                inputMFARef?.current?.classList.remove("is-invalid");
                            }}></input>
                            <button onClick={e => { e.preventDefault(); submitCode(); }} type="submit" className="btn btn-secondary">Submit</button>
                        </div>
                    </div>
                </div>
            </form>
        );
    }

    const remove = async () => {
        if (inputMFA != '' && inputMFA.length > 0) {
            try {
                var res = await API_AccountSecurityRemoveMfaType({ Type: props.baseState.Type.Type, Code: inputMFA });
                if (res.Completed) {
                    inputMFARemoveRef?.current?.classList.remove("is-invalid");
                    inputMFARemoveRef?.current?.classList.remove("is-valid");
                    setUserMFAVerified(false);
                    setSetupTypeResponseModel(null);
                    setErrors([]);
                    toast.success("Removed");
                    await props.reload();
                }
                else {
                    inputMFARemoveRef?.current?.classList.add("is-invalid");
                    inputMFARemoveRef?.current?.classList.remove("is-valid");
                    setErrors(["Code is invalid"]);
                }
            }
            catch {
                setErrors(["Error removing link"]);
                toast.error("Error");
            }
            finally {
                setInputMFA('');
            }
        } else {
            inputMFARemoveRef?.current?.classList.add("is-invalid");
            inputMFARemoveRef?.current?.classList.remove("is-valid");
            setErrors(["Please provide a code"]);
            setInputMFA('');
        }

    }

    if (userMFAVerified && canBeRemoved) {
        return (
            <form>
                <h5 className="text-alt-or-contrast">App authentication linked</h5>
                <p>If you wish to remove the authentication method please enter a code and click the button below.</p>

                <ErrorsDisplay errors={errors} />

                <label htmlFor="CodeInput" className="form-label">Code</label>
                <div className="input-group">
                    <input type="text" ref={inputMFARemoveRef} className="form-control" placeholder="Code" id="CodeInput" onChange={t => {
                        setInputMFA(t.currentTarget.value);
                        inputMFARemoveRef?.current?.classList.remove("is-valid");
                        inputMFARemoveRef?.current?.classList.remove("is-invalid");
                    }}></input>
                    <button onClick={e => { e.preventDefault(); remove(); }} type="submit" className="btn btn-danger">Remove</button>
                </div>
            </form>
        );
    }
    else {
        return (
            <>
                <h5 className="text-alt-or-contrast">App authentication linked</h5>
                <p>If you wish to remove the authentication please contact support.</p>
            </>
        );
    }
}